<script setup>
  import { useRoute, useRouter } from 'vue-router'
  import { watch, computed, reactive, onMounted, onUnmounted } from 'vue'
  import { useTemplateStore } from '@/stores/template'
  import { useAlert } from '@/composables/useAlert'
  import { usePermissions } from '@/composables/usePermissions'
  import { createLogger } from '@/shared/logger'
  import BaseLayout from '@/layouts/BaseLayout.vue'

  // Centralized error messages
  const ERROR_MESSAGES = {
    NO_SITE_ACCESS: 'You do not have access to any site',
    INVALID_SITE: site => `Invalid site: ${site}`,
    REDIRECTING_TO: redirectSite => `Redirecting you to: ${redirectSite}`,
    SITE_LOAD_ERROR: 'Unable to load site data. Please try again.',
    INITIAL_DATA_LOAD_ERROR:
      'Unable to load initial site data. Please try again.'
  }

  // Main store, Router and Services
  const router = useRouter()
  const route = useRoute()
  const store = useTemplateStore()
  const alertService = useAlert()
  const logger = createLogger('PublinkerBackend')
  const { loadUserPermissions } = usePermissions()

  // Specific loading states
  const loadingStates = reactive({
    permissions: false,
    siteConfig: false,
    companyData: false
  })

  // Computed properties for common checks
  const currentSite = computed(() => route.params.site)
  const defaultSite = computed(() => store.site || store.siteList[0])
  const isValidSite = computed(
    () => currentSite.value && store.siteList.includes(currentSite.value)
  )
  const needsSiteChange = computed(
    () => store.site !== currentSite.value && currentSite.value
  )

  /**
   * Checks user permissions and manages site access
   * Redirects to a valid site if necessary
   * @returns {void}
   */
  const checkPermissions = () => {
    if (store.permissions === null) return

    // Diagnostic logs to analyze parameters after login
    logger.log('***** checkPermissions - Route parameters:', {
      currentSite: currentSite.value,
      routeFullPath: route.fullPath,
      routeParams: route.params,
      isValidSite: isValidSite.value,
      defaultSite: defaultSite.value,
      siteList: store.siteList,
      storeSite: store.site
    })

    // Checks if the user has access to at least one site
    if (store.siteList.length === 0) {
      alertService.showAlert(
        'Not authorized',
        ERROR_MESSAGES.NO_SITE_ACCESS,
        'warning'
      )
      return
    }

    // Checks if there is a saved site from recent login
    const savedLoginSite = localStorage.getItem('publinker_login_target_site')
    const savedLoginPath = localStorage.getItem('publinker_login_target_path')
    const savedLoginTimestamp = localStorage.getItem(
      'publinker_login_timestamp'
    )
    const isRecentLogin =
      savedLoginTimestamp && store.isTimestampRecent(savedLoginTimestamp)

    if (
      savedLoginSite &&
      isRecentLogin &&
      store.siteList.includes(savedLoginSite)
    ) {
      logger.log('***** Recent login site found and valid:', savedLoginSite)
      logger.log('***** Recent login path:', savedLoginPath)
      setSite(savedLoginSite, savedLoginPath)
      return
    }

    // Manages different navigation cases for sites
    if (!currentSite.value) {
      // No site in URL, uses the default
      logger.log('***** No site in URL, using default:', defaultSite.value)
      setSite(defaultSite.value)
      return
    } else if (currentSite.value === 'login') {
      // Specific login case, redirects to default site
      logger.log(
        '***** Site is "login", redirecting to default:',
        defaultSite.value
      )
      setSite(defaultSite.value)
      return
    } else if (!isValidSite.value) {
      // Invalid site, displays alert and redirects
      logger.log(
        '***** Invalid site:',
        currentSite.value,
        'redirecting to:',
        store.siteList[0]
      )
      alertService.showAlert(
        ERROR_MESSAGES.INVALID_SITE(currentSite.value),
        ERROR_MESSAGES.REDIRECTING_TO(store.siteList[0]),
        'warning'
      )
      setSite(store.siteList[0])
      return
    } else if (needsSiteChange.value) {
      // Valid site but different from current, updates
      logger.log(
        '***** Valid site but different from current, updating to:',
        currentSite.value
      )
      setSite(currentSite.value)
      return
    }

    // Keeps current site if already correct
    logger.log('***** Keeping current site:', store.site)
    setSite(store.site)
  }

  /**
   * Fetches user permissions from the server
   * @returns {Promise<void>}
   */
  const fetchUserPermissions = async () => {
    try {
      loadingStates.permissions = true
      store.pageLoader({ mode: 'on' })

      // Usa o método do composable usePermissions
      await loadUserPermissions()

      checkPermissions()
    } catch (error) {
      logger.error('Error fetching permissions:', error)
    } finally {
      loadingStates.permissions = false
    }
  }

  // Starts loading permissions
  fetchUserPermissions()

  // Define o intervalo para atualizar as permissões a cada 15 minutos
  // Isso garante que as mudanças de permissões no Auth0 sejam refletidas sem precisar de um novo login
  const PERMISSIONS_REFRESH_INTERVAL = 15 * 60 * 1000 // 15 minutos
  let permissionsRefreshInterval = null

  onMounted(() => {
    // Inicia o intervalo de atualização de permissões
    permissionsRefreshInterval = setInterval(() => {
      logger.log('Refreshing user permissions...')
      // Usar force=true para garantir que as permissões sejam recarregadas mesmo que sejam recentes
      loadUserPermissions(true).catch(error => {
        logger.error('Error refreshing permissions:', error)
      })
    }, PERMISSIONS_REFRESH_INTERVAL)
  })

  // Limpa o intervalo quando o componente é desmontado
  onUnmounted(() => {
    if (permissionsRefreshInterval) {
      clearInterval(permissionsRefreshInterval)
    }
  })

  // Watches for changes in the site parameter of the URL
  watch(
    () => route.params.site,
    (site, oldSite) => {
      if (site && site !== oldSite) {
        logger.log('Route site change:', 'new:', site, 'old:', oldSite)
        checkPermissions()
      }
    }
  )

  /**
   * Sets the current site and updates the route
   * @param {string} site - The site identifier to be set
   * @param {string} [path] - The optional path to be used (if omitted, keeps current path)
   * @returns {void}
   */
  const setSite = (site, path) => {
    store.site = site

    // Diagnostic logs
    logger.log('***** setSite - Parameters:', {
      site: site,
      pathParameter: path,
      currentRouteName: route.name,
      currentPath: route.path,
      currentFullPath: route.fullPath
    })

    // Checks if there is a saved path from recent login
    const savedLoginPath = localStorage.getItem('publinker_login_target_path')
    const savedLoginTimestamp = localStorage.getItem(
      'publinker_login_timestamp'
    )
    const isRecentLogin =
      savedLoginTimestamp && store.isTimestampRecent(savedLoginTimestamp)

    // If a path is explicitly passed, uses it
    // Otherwise, checks the saved path from login
    // If nothing is found, keeps the current route
    let targetPath = path

    if (!targetPath && savedLoginPath && isRecentLogin) {
      logger.log('***** Using saved path from login:', savedLoginPath)
      targetPath = savedLoginPath

      // Clears data to avoid repeated use
      localStorage.removeItem('publinker_login_target_path')
      localStorage.removeItem('publinker_login_timestamp')
    }

    if (targetPath) {
      // If there is a specific path, uses it to build the URL
      logger.log('***** Navigating to specific path:', targetPath)

      // Checks if the route contains additional parameters
      const hasParams = targetPath.includes(':')

      if (hasParams) {
        // Expected format: "route:param1=value1:param2=value2"
        const [routeName, ...paramParts] = targetPath.split(':')
        const params = { site }

        paramParts.forEach(part => {
          const [key, value] = part.split('=')
          if (key && value) params[key] = value
        })

        router.replace({
          name: routeName || 'backend-dashboards',
          params
        })
      } else {
        // Simple navigation to a specific path
        router.push(`/${site}/${targetPath}`)
      }
    } else {
      // Keeps the same route name, just updates the site
      logger.log('***** Keeping same route, updating only site to:', site)
      router.replace({
        name: route.name,
        params: {
          site: site
        }
      })
    }
  }

  /**
   * Loads site and company data
   * @param {string} site - The site identifier to load data for
   * @returns {Promise<void>}
   */
  const loadSiteData = async site => {
    try {
      loadingStates.siteConfig = true
      loadingStates.companyData = true
      store.pageLoader({ mode: 'on' })
      store.abortRequests()
      store.siteDataLoaded = false // Reseta a flag antes de começar o carregamento

      // Loads data in parallel
      await Promise.all([
        store.fetchConfig(site).finally(() => {
          loadingStates.siteConfig = false
        }),
        store.fetchCompanyBySite(site).finally(() => {
          loadingStates.companyData = false
        })
      ])

      // Atualiza a flag ao invés de disparar o evento
      store.siteDataLoaded = true
    } catch (error) {
      logger.error('Error loading site data:', error)
      showErrorDialog('Error Loading Site', ERROR_MESSAGES.SITE_LOAD_ERROR)
    } finally {
      store.pageLoader({ mode: 'off' })
    }
  }

  /**
   * Displays a standardized error dialog
   * @param {string} title - Error dialog title
   * @param {string} message - Error message to be displayed
   * @returns {Promise<void>}
   */
  const showErrorDialog = async (title, message) => {
    const result = await alertService.showConfirm({
      title,
      text: message,
      icon: 'error',
      confirmButtonText: 'Try Again',
      cancelButtonText: 'Close'
    })

    if (result.isConfirmed) {
      window.location.reload()
    }
  }

  // Loads site data for the current site, if available
  if (store.site) {
    loadSiteData(store.site).catch(error => {
      logger.error('Error loading initial site data:', error)
      store.pageLoader({ mode: 'off' })
      showErrorDialog(
        'Error Loading Data',
        ERROR_MESSAGES.INITIAL_DATA_LOAD_ERROR
      )
    })
  }

  // Watches for changes in the selected site
  watch(
    () => store.site,
    (site, oldSite) => {
      if (site !== oldSite) {
        logger.log('Store site change:', 'new:', site, 'old:', oldSite)
        loadSiteData(site)
      }
    }
  )

  // Configures the layout
  /**
   * Configures the default layout for the backend
   */
  const setupBackendLayout = () => {
    // Set default elements for this layout
    store.setLayout({
      header: true,
      sidebar: true,
      sideOverlay: true,
      footer: true
    })

    // Set template options
    store.mainContent({ mode: 'full' })
    store.sidebarMini({ mode: 'on' })
    store.header({ mode: 'static' })
  }

  setupBackendLayout()
</script>

<template>
  <BaseLayout @set-site="setSite" />
</template>
